import React from 'react';
import { styled, alpha } from '@mui/system';
import { Typography, Box, useTheme } from '@mui/material';
import RightIcon from '@mui/icons-material/ChevronRight';
import { Link } from 'react-router-dom';

import TroubleshootOutlinedIcon from '@mui/icons-material/TroubleshootOutlined';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import InventoryIcon from '@mui/icons-material/Inventory';

import { useSelector } from 'react-redux';
import { RootStateType } from '@Store';
import { isActionAllowed } from '../../utils/allowedActions';
import { ACTION_NAMES, PERFORM_ACTION } from '@Constants/actionsNames';
import { userLogin } from '@Services/hasAdminAccess';

/* -------------------- CONTAINER -------------------- */

const NavigationBtnContainer = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
  gap: '24px',

  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
    gap: '16px',
  },
}));

/* -------------------- CARD -------------------- */

const NavigationBtn = styled(Link, {
  shouldForwardProp: prop => prop !== 'disabled',
})<{ disabled?: boolean }>(({ theme, disabled }) => ({
  textDecoration: 'none',
  color: 'inherit',

  height: '130px',
  padding: '22px',

  display: 'grid',
  gridTemplateColumns: '52px 1fr 24px',
  alignItems: 'center',
  gap: '16px',

  borderRadius: '14px',

  /* ✅ THEME BORDER (MAIN CHANGE) */
  border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,

  background: disabled ? alpha(theme.palette.background.paper, 0.7) : alpha(theme.palette.background.paper, 0.85),

  backdropFilter: 'blur(10px)',

  cursor: disabled ? 'not-allowed' : 'pointer',
  // opacity: disabled ? 1 : 1,

  boxShadow: '0 2px 10px rgba(0,0,0,0.04)',

  transition: 'all 0.25s ease-in-out',

  position: 'relative',
  overflow: 'hidden',

  /* hover shine effect */
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-120%',
    width: '60%',
    height: '100%',
    background: `linear-gradient(
      90deg,
      transparent,
      ${alpha(theme.palette.primary.main, 0.15)},
      transparent
    )`,
    transform: 'skewX(-25deg)',
    transition: '0.7s',
  },

  '&:hover': !disabled && {
    transform: 'translateY(-3px)',
    border: `1px solid ${alpha(theme.palette.primary.main, 0.35)}`,
    boxShadow: `0 12px 30px ${alpha(theme.palette.primary.main, 0.12)}`,

    '&::before': {
      left: '140%',
    },
  },

  '&:active': !disabled && {
    transform: 'translateY(0px)',
  },
}));

/* -------------------- ICON BOX -------------------- */

const IconContainer = styled('div')<{ disabled?: boolean }>(({ theme, disabled }) => ({
  width: '48px',
  height: '48px',

  borderRadius: '12px',

  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  background: disabled
    ? alpha(theme.palette.primary.main, 0.08)
    : `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,

  color: '#fff',

  boxShadow: `0 8px 18px ${alpha(theme.palette.primary.main, 0.25)}`,
}));

/* -------------------- MAIN -------------------- */

function Navigation() {
  const { menuAcccessGroupById: accessGroupById } = useSelector((state: RootStateType) => state.accessGroup);

  const isUserLogin = userLogin();
  const theme = useTheme();

  const isGetRatesAllowed = isActionAllowed(
    accessGroupById?.actions,
    ACTION_NAMES.Shipment_Details.get_rates,
    PERFORM_ACTION.write,
  );

  const isCreateBulkShipmentsAllowed = isActionAllowed(
    accessGroupById?.actions,
    ACTION_NAMES.Shipment_Details.create_bulk_shipments,
    PERFORM_ACTION.write,
  );

  const isCreateSingleShipmentAllowed = isActionAllowed(
    accessGroupById?.actions,
    ACTION_NAMES.Shipment_Details.create_single_shipment,
    PERFORM_ACTION.write,
  );

  const navigationItems = [
    {
      title: 'Get Rates',
      path: '/get-rates',
      icon: <TroubleshootOutlinedIcon sx={{ fontSize: 24 }} />,
      allowed: !isUserLogin ? isGetRatesAllowed : true,
      description: 'Compare shipping rates',
    },
    {
      title: 'Place a Single Shipment',
      path: '/shipment/create-single',
      icon: <LocalShippingIcon sx={{ fontSize: 24 }} />,
      allowed: !isUserLogin ? isCreateSingleShipmentAllowed : true,
      description: 'Create individual shipment',
    },
    {
      title: 'Place a Bulk Shipment',
      path: '/shipment/create-bulk',
      icon: <InventoryIcon sx={{ fontSize: 24 }} />,
      allowed: !isUserLogin ? isCreateBulkShipmentsAllowed : true,
      description: 'Create multiple shipments',
    },
  ];

  return (
    <NavigationBtnContainer>
      {navigationItems.map((item, index) => (
        <NavigationBtn
          key={index}
          to={item.allowed ? item.path : ''}
          disabled={!item.allowed}
          onClick={e => !item.allowed && e.preventDefault()}
        >
          <IconContainer disabled={!item.allowed}>{item.icon}</IconContainer>

          <Box>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: '16px',
                mb: 0.5,
                color: !item.allowed ? alpha(theme.palette.text.primary, 0.65) : theme.palette.text.primary,
              }}
            >
              {item.title}
            </Typography>

            <Typography
              sx={{
                fontSize: '14px',
                color: !item.allowed ? alpha(theme.palette.text.secondary, 0.6) : theme.palette.text.secondary,
              }}
            >
              {item.description}
            </Typography>
          </Box>

          <RightIcon
            sx={{
              fontSize: 20,
              color: !item.allowed ? 'action.disabled' : 'primary.main',
              justifySelf: 'flex-end',
              transition: 'transform 0.2s ease',
              '.MuiLink-root:hover &': !item.allowed
                ? {}
                : {
                    transform: 'translateX(4px)',
                  },
            }}
          />
          {!item.allowed && (
            <Typography
              sx={{
                position: 'absolute',
                top: 10,
                right: 10,
                fontSize: '11px',
                px: 1,
                py: 0.3,
                borderRadius: '6px',
                bgcolor: 'action.disabledBackground',
                color: 'text.secondary',
              }}
            >
              Restricted
            </Typography>
          )}
        </NavigationBtn>
      ))}
    </NavigationBtnContainer>
  );
}

export default Navigation;
